import { Alert } from "@/src/lib/productAlert";
import { Text, TextInput as BrandTextInput } from "@/src/components/ui/BrandText";
import { border, brand, surface, text as uiText } from "@/src/theme/tokens";
import { useAuth } from "@/src/hooks/useAuth";
import {
  createChatMessageId,
  getChatErrorMessage,
  getConversationDeletedAt,
  markConversationRead,
  sendMessage,
  toMillis,
} from "@/src/lib/chat";
import { useBlockedUsers } from "@/src/hooks/useBlockedUsers";
import { blockUser, unblockUser } from "@/src/lib/blocks";
import { reportUser } from "@/src/lib/reports";
import { getPublicProfilesByIds } from "@/src/lib/publicProfiles";
import { getPrimaryIdentityBadge, getPublicAuthorSnapshot } from "@/src/lib/identity";
import { IdentityHeader } from "@/src/components/IdentityHeader";
import { avatarSourceFor } from "@/src/theme/defaultAvatars";
import type { AccountType, IdentityEntitlements, LegacyAccountType, PublicIdentitySnapshot, VerificationStatus } from "@/src/types/identity";
import { Ionicons } from "@/src/components/ui/ionicons";
import { router, useLocalSearchParams } from "expo-router";
import {
  collection,
  doc,
  getDocs,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
} from "firebase/firestore";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  Image,
  InteractionManager,
  Keyboard,
  KeyboardAvoidingView,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  Pressable,
  View,
  LayoutChangeEvent,
} from "react-native";
import { SafeAreaView, useSafeAreaInsets } from "react-native-safe-area-context";

const BG = surface.secondary;
const ACCENT = brand.primary;
const CARD = surface.primary;
const BORDER = border.subtle;
const PAGE_SIZE = 20;
const BOTTOM_THRESHOLD = 120;

type Message = {
  id: string;
  senderUid: string;
  text: string;
  type?: "text" | "shared_post";
  sharedPost?: {
    postId: string;
    authorIdentityId?: string;
    authorSnapshot?: PublicIdentitySnapshot;
    authorUsername?: string;
    authorEmail?: string;
    authorAccountType?: AccountType;
    authorVerificationStatus?: VerificationStatus;
    authorEntitlements?: IdentityEntitlements;
    /** @deprecated Compatibility snapshot for historical student/teacher posts. */
    authorRole?: LegacyAccountType;
    subject?: string;
    text?: string;
    groupName?: string;
    imageUrl?: string;
    attachmentType?: "image" | "pdf";
    attachmentName?: string;
  };
  createdAt?: any;
};

function displaySenderLabel(user?: {
  username?: string;
  displayName?: string;
}) {
  if (typeof user?.displayName === "string" && user.displayName.trim().length > 0) {
    return user.displayName.trim();
  }
  if (typeof user?.username === "string" && user.username.trim().length > 0) {
    return `@${user.username.replace(/^@/, "")}`;
  }
  return "";
}

function isSameMessageDay(a: Message | null, b: Message | null) {
  const aMs = toMillis(a?.createdAt);
  const bMs = toMillis(b?.createdAt);
  if (!aMs || !bMs) return false;
  const left = new Date(aMs);
  const right = new Date(bMs);
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

function formatDateSeparator(ts: any) {
  const ms = toMillis(ts);
  if (!ms) return "";
  return new Date(ms).toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

const db = getFirestore();

function formatMessageTime(ts: any) {
  const ms = toMillis(ts);
  if (!ms) return "";

  const d = new Date(ms);
  return d.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

type MessageRow = {
  item: Message;
  mine: boolean;
  sameAsPrev: boolean;
  sameAsNext: boolean;
  showSender: boolean;
  senderLabel: string;
  showTime: boolean;
  dateLabel: string;
};

const MessageBubble = React.memo(function MessageBubble({ row }: { row: MessageRow }) {
  const { item, mine, sameAsPrev, sameAsNext, showSender, senderLabel, showTime, dateLabel } = row;
  const sharedIdentity = item.sharedPost ? getPublicAuthorSnapshot(item.sharedPost) : null;
  return (
    <View>
      {dateLabel ? (
        <View style={{ alignItems: "center", paddingVertical: 10 }}>
          <Text
            style={{
              color: uiText.muted,
              fontSize: 11,
              fontWeight: "800",
              textTransform: "capitalize",
            }}
          >
            {dateLabel}
          </Text>
        </View>
      ) : null}
      <View
        style={{
          alignItems: mine ? "flex-end" : "flex-start",
          marginBottom: sameAsNext ? 4 : 10,
          marginTop: sameAsPrev ? 0 : 6,
        }}
      >
        {showSender ? (
          <Text
            style={{
              color: ACCENT,
              fontSize: 12,
              fontWeight: "800",
              marginBottom: 4,
              paddingHorizontal: 4,
            }}
          >
            {senderLabel}
          </Text>
        ) : null}
        <View
          style={{
            maxWidth: "78%",
            backgroundColor: mine ? ACCENT : surface.primary,
            borderRadius: 16,
            borderTopRightRadius: mine && sameAsPrev ? 8 : 16,
            borderTopLeftRadius: !mine && sameAsPrev ? 8 : 16,
            borderWidth: 1,
            borderColor: mine ? surface.selected : BORDER,
            overflow: "hidden",
          }}
        >
        {item.type === "shared_post" && item.sharedPost?.postId ? (
          <View style={{ padding: 12, gap: 10 }}>
            <Text style={{ color: uiText.muted, fontSize: 12, fontWeight: "800" }}>
              Publication partagée
            </Text>

            {sharedIdentity ? (
              <IdentityHeader
                identity={sharedIdentity}
                compact
                onPress={() => router.push(`/profile/${sharedIdentity.identityId}`)}
              />
            ) : null}

            <Pressable
              onPress={() => router.push(`/post/${item.sharedPost?.postId}`)}
              accessibilityRole="button"
              accessibilityLabel="Ouvrir la publication partagée"
              style={{
                borderRadius: 12,
                borderWidth: 1,
                borderColor: border.subtle,
                backgroundColor: surface.overlay,
                overflow: "hidden",
              }}
            >
              {item.sharedPost.imageUrl ? (
                <Image
                  source={{ uri: item.sharedPost.imageUrl }}
                  style={{ width: "100%", height: 150, backgroundColor: surface.primary }}
                  resizeMode="cover"
                />
              ) : null}

              <View style={{ padding: 12 }}>
                {!sharedIdentity ? (
                  <Text style={{ color: uiText.primary, fontWeight: "800" }}>
                    {item.sharedPost.authorUsername
                      ? `@${String(item.sharedPost.authorUsername).replace(/^@/, "")}`
                      : "Utilisateur"}
                  </Text>
                ) : null}

                <Text style={{ color: uiText.muted, marginTop: 4 }} numberOfLines={1}>
                  {[
                    item.sharedPost.subject,
                    item.sharedPost.groupName,
                  ]
                    .filter(Boolean)
                    .join(" | ")}
                </Text>

                {!!item.sharedPost.text ? (
                  <Text style={{ color: uiText.primary, marginTop: 8, lineHeight: 20 }} numberOfLines={4}>
                    {item.sharedPost.text}
                  </Text>
                ) : null}

                {item.sharedPost.attachmentType === "pdf" ? (
                  <Text style={{ color: uiText.muted, marginTop: 8 }}>
                    Pièce jointe : {item.sharedPost.attachmentName || "Document"}
                  </Text>
                ) : null}
              </View>
            </Pressable>
          </View>
        ) : (
          <View style={{ paddingHorizontal: 12, paddingVertical: 10 }}>
            <Text style={{ color: uiText.primary, lineHeight: 20 }}>{item.text}</Text>
          </View>
        )}
        </View>

        {showTime ? (
          <Text
            style={{
              marginTop: 4,
              fontSize: 11,
              color: uiText.muted,
              paddingHorizontal: 4,
            }}
          >
            {formatMessageTime(item.createdAt)}
          </Text>
        ) : null}
      </View>
    </View>
  );
}, areMessageRowsEqual);

function areMessageRowsEqual(prevProps: { row: MessageRow }, nextProps: { row: MessageRow }) {
  const previous = prevProps.row;
  const next = nextProps.row;
  return (
    previous.item === next.item &&
    previous.mine === next.mine &&
    previous.sameAsPrev === next.sameAsPrev &&
    previous.sameAsNext === next.sameAsNext &&
    previous.showSender === next.showSender &&
    previous.senderLabel === next.senderLabel &&
    previous.showTime === next.showTime &&
    previous.dateLabel === next.dateLabel
  );
}

const ChatComposer = React.memo(function ChatComposer({
  onSendText,
  scrollToBottom,
  disabled,
  disabledLabel,
  bottomInset,
  onLayout,
}: {
  onSendText: (text: string, messageId: string) => Promise<boolean>;
  scrollToBottom: (animated: boolean) => void;
  disabled?: boolean;
  disabledLabel?: string;
  bottomInset: number;
  onLayout?: (event: LayoutChangeEvent) => void;
}) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const pendingMessageIdRef = useRef<string | null>(null);
  const focusScrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const trimmed = text.trim();

  useEffect(() => {
    return () => {
      if (focusScrollTimerRef.current) clearTimeout(focusScrollTimerRef.current);
    };
  }, []);

  const handleSend = useCallback(async () => {
    if (!trimmed || sending) return;
      const value = trimmed;
      const messageId = pendingMessageIdRef.current ?? createChatMessageId();
      setSending(true);

      try {
        const sent = await onSendText(value, messageId);
        if (sent) {
          pendingMessageIdRef.current = null;
          setText("");
        } else {
          pendingMessageIdRef.current = messageId;
          setText((current) => (current.trim().length === 0 ? value : current));
        }
      } finally {
        setSending(false);
      }
  }, [trimmed, sending, onSendText]);

  return (
    <View
      onLayout={onLayout}
      style={{
        borderTopWidth: 1,
        borderTopColor: BORDER,
        padding: 12,
        paddingBottom: Math.max(12, bottomInset),
        backgroundColor: BG,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-end",
          gap: 10,
        }}
      >
        <BrandTextInput
          value={text}
          onChangeText={(nextText) => {
            if (pendingMessageIdRef.current && nextText.trim() !== trimmed) {
              pendingMessageIdRef.current = null;
            }
            setText(nextText);
          }}
          editable={!disabled}
          onFocus={() => {
            if (focusScrollTimerRef.current) clearTimeout(focusScrollTimerRef.current);
            focusScrollTimerRef.current = setTimeout(() => {
              focusScrollTimerRef.current = null;
              scrollToBottom(false);
            }, 120);
          }}
          placeholder={disabled ? disabledLabel || "Vous ne pouvez pas écrire ici." : "Écrire un message…"}
          placeholderTextColor={uiText.muted}
          multiline
          style={{
            flex: 1,
            minHeight: 44,
            maxHeight: 120,
            color: uiText.primary,
            backgroundColor: surface.primary,
            borderWidth: 1,
            borderColor: BORDER,
            borderRadius: 14,
            paddingHorizontal: 14,
            paddingVertical: 12,
            textAlignVertical: "top",
          }}
        />

        <Pressable
          onPress={handleSend}
          disabled={!trimmed || disabled || sending}
          style={{
            height: 44,
            paddingHorizontal: 16,
            borderRadius: 12,
            backgroundColor:
              trimmed && !disabled && !sending ? ACCENT : surface.selected,
            alignItems: "center",
            justifyContent: "center",
            minWidth: 86,
          }}
        >
          <Text style={{ color: uiText.primary, fontWeight: "800" }}>
            {sending ? "Envoi…" : "Envoyer"}
          </Text>
        </Pressable>
      </View>
    </View>
  );
});

function LegacyDirectArchiveNotice() {
  return (
    <View style={{ borderTopWidth: 1, borderTopColor: BORDER, padding: 14, gap: 6, backgroundColor: BG }}>
      <Text style={{ color: uiText.primary, fontWeight: "900" }}>Archive de conversation</Text>
      <Text style={{ color: uiText.muted, lineHeight: 19 }}>
        Cet ancien fil est rattaché à un compte Firebase et reste consultable en lecture seule. Les nouvelles discussions utilisent une identité publique.
      </Text>
      <Pressable onPress={() => router.replace("/(tabs)/messages")} accessibilityRole="button" style={{ alignSelf: "flex-start", minHeight: 40, justifyContent: "center", paddingHorizontal: 12, borderRadius: 11, backgroundColor: surface.selected }}>
        <Text style={{ color: uiText.primary, fontWeight: "800" }}>Retour aux messages</Text>
      </Pressable>
    </View>
  );
}

export default function ChatRoom() {
  const insets = useSafeAreaInsets();
  const composerBottomInset = Platform.OS === "ios" ? insets.bottom : 0;
  const { id } = useLocalSearchParams<{ id: string }>();
  const conversationId = useMemo(() => String(id || ""), [id]);
  const { user } = useAuth();

  const [messages, setMessages] = useState<Message[]>([]);
  const [oldestDoc, setOldestDoc] = useState<any>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [composerHeight, setComposerHeight] = useState(76);
  const [listHeight, setListHeight] = useState(0);
  const [topSpacerHeight, setTopSpacerHeight] = useState(0);
  const [androidKeyboardHeight, setAndroidKeyboardHeight] = useState(0);

  const [conversationData, setConversationData] = useState<any>(null);
  const [userMap, setUserMap] = useState<Record<string, any>>({});
  const [deletedAtMs, setDeletedAtMs] = useState(0);
  const [chatError, setChatError] = useState<string | null>(null);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const [linkedGroupData, setLinkedGroupData] = useState<any>(null);
  const isGroup = conversationData?.type === "group";
  const groupPostingMode =
    conversationData?.whoCanPost === "admins_only" ? "admins_only" : "all_members";
  const groupAdmins = Array.isArray(conversationData?.admins) ? conversationData.admins : [];
  const blockedUsers = useBlockedUsers(user?.uid);
  const memberIdsKey = useMemo(
    () =>
      Array.isArray(conversationData?.members)
        ? Array.from(new Set(conversationData.members.filter(Boolean))).sort().join("|")
        : "",
    [conversationData?.members]
  );
  const memberIds = useMemo(
    () => (memberIdsKey ? memberIdsKey.split("|") : []),
    [memberIdsKey]
  );

  const linkedGroupId =
    typeof conversationData?.linkedGroupId === "string" ? conversationData.linkedGroupId.trim() : "";

  useEffect(() => {
    if (!isGroup) return;
    if (linkedGroupId) {
      router.replace({
        pathname: "/(tabs)/messages/group-thread/[id]",
        params: { id: linkedGroupId },
      });
      return;
    }
    router.replace("/(tabs)/messages");
  }, [isGroup, linkedGroupId]);

  useEffect(() => {
    if (!linkedGroupId) {
      setLinkedGroupData(null);
      return;
    }

    return onSnapshot(
      doc(db, "groups", linkedGroupId),
      (snap) => setLinkedGroupData(snap.exists() ? snap.data() : null),
      () => setLinkedGroupData(null)
    );
  }, [linkedGroupId]);

  // The conversation document is already observed in realtime. Reuse it for
  // the header instead of issuing a second conversation read on every open.
  const otherUser = useMemo(() => {
    if (isGroup || !user?.uid) return null;
    const otherUid = memberIds.find((memberUid) => memberUid !== user.uid);
    return otherUid ? userMap[otherUid] ?? null : null;
  }, [isGroup, memberIds, user?.uid, userMap]);

  const otherUid = useMemo(
    () => (!isGroup && user?.uid ? memberIds.find((memberUid) => memberUid !== user.uid) || "" : ""),
    [isGroup, memberIds, user?.uid]
  );
  const otherUserBlocked = !!otherUid && blockedUsers.has(otherUid);
  const canCurrentUserPost =
    isGroup && (groupPostingMode === "all_members" || (!!user?.uid && groupAdmins.includes(user.uid)));

  const chatTitle = useMemo(() => {
    if (isGroup) {
      return linkedGroupData?.name || conversationData?.name || conversationData?.title || "Groupe indisponible";
    }
    return displaySenderLabel(otherUser) || (otherUid ? "Utilisateur indisponible" : "Conversation indisponible");
  }, [conversationData?.name, conversationData?.title, isGroup, linkedGroupData?.name, otherUid, otherUser]);

  const groupMemberCount =
    isGroup && Number.isInteger(linkedGroupData?.membersCount)
      ? linkedGroupData.membersCount
      : Array.isArray(conversationData?.members)
      ? conversationData.members.length
      : 0;


  const listRef = useRef<FlatList<MessageRow>>(null);

  const didInitialScrollRef = useRef(false);
  const pendingInitialScrollRef = useRef(false);
  const pendingAutoScrollRef = useRef(false);
  const isNearBottomRef = useRef(true);
  const lastMessageIdRef = useRef<string | null>(null);
  const lastReadMessageIdRef = useRef<string | null>(null);
  const newestMessageRef = useRef<Message | null>(null);
  const readRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const oldestDocRef = useRef<any>(null);
  const loadedOlderRef = useRef(false);
  const loadingOlderRef = useRef(false);
  const forceScrollAfterSendRef = useRef(false);
  const scrollButtonVisibleRef = useRef(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const spacerHeightRef = useRef(0);


  useEffect(() => {
    if (!conversationId || !user?.uid) {
      setDeletedAtMs(0);
      return;
    }

    let cancelled = false;

    getConversationDeletedAt(user.uid, conversationId)
      .then((value) => {
        if (!cancelled) {
          setDeletedAtMs(value);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDeletedAtMs(0);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [conversationId, user?.uid]);

  useEffect(() => {
    if (!memberIds.length) {
      setUserMap({});
      return;
    }

    let cancelled = false;

    getPublicProfilesByIds(memberIds).then((profilesById) => {
      if (!cancelled) {
        const entries = memberIds.map((uid) => [
          uid,
          profilesById[uid] ?? {},
        ]);
        setUserMap(Object.fromEntries(entries));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [memberIds, memberIdsKey]);


  useEffect(() => {
    const timeoutId = scrollTimeoutRef.current;
    const retryTimerId = readRetryTimerRef.current;

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (retryTimerId) clearTimeout(retryTimerId);
      scrollTimeoutRef.current = null;
      readRetryTimerRef.current = null;
    };
  }, []);

  useEffect(() => {
    loadingOlderRef.current = loadingOlder;
  }, [loadingOlder]);

  const scrollToBottomNow = useCallback((animated: boolean) => {
    listRef.current?.scrollToEnd({ animated });
  }, []);

  const scrollToBottom = useCallback(
    (animated: boolean) => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      InteractionManager.runAfterInteractions(() => {
        requestAnimationFrame(() => {
          scrollToBottomNow(animated);
        });
      });
    },
    [scrollToBottomNow]
  );

  const scheduleScroll = useCallback(
    (delay: number) => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        scrollTimeoutRef.current = null;
        scrollToBottom(false);
      }, delay);
    },
    [scrollToBottom]
  );

  const markReadIfAtBottom = useCallback(() => {
    const newestMessage = newestMessageRef.current;
    if (
      !conversationId ||
      !user?.uid ||
      !isNearBottomRef.current ||
      !newestMessage?.id ||
      newestMessage.senderUid === user.uid ||
      lastReadMessageIdRef.current === newestMessage.id
    ) {
      return;
    }

    lastReadMessageIdRef.current = newestMessage.id;
    Promise.resolve(markConversationRead(conversationId, user.uid)).then(
      () => {
        const latest = newestMessageRef.current;
        if (
          latest?.id !== newestMessage.id &&
          isNearBottomRef.current &&
          !readRetryTimerRef.current
        ) {
          readRetryTimerRef.current = setTimeout(() => {
            readRetryTimerRef.current = null;
            lastReadMessageIdRef.current = null;
            const currentLatest = newestMessageRef.current;
            if (
              currentLatest?.id &&
              isNearBottomRef.current &&
              currentLatest.senderUid !== user.uid
            ) {
              lastReadMessageIdRef.current = currentLatest.id;
              markConversationRead(conversationId, user.uid).catch(() => {
                lastReadMessageIdRef.current = null;
              });
            }
          }, 1600);
        }
      },
      () => {
        if (newestMessageRef.current?.id === newestMessage.id) {
          lastReadMessageIdRef.current = null;
        }
      }
    );
  }, [conversationId, user?.uid]);

  const updateBottomState = useCallback((distanceFromBottom: number) => {
    const nearBottom = distanceFromBottom < BOTTOM_THRESHOLD;
    isNearBottomRef.current = nearBottom;

    const nextShow = !nearBottom;
    if (nearBottom) {
      setHasNewMessages(false);
      markReadIfAtBottom();
    }
    if (scrollButtonVisibleRef.current !== nextShow) {
      scrollButtonVisibleRef.current = nextShow;
      setShowScrollToBottom(nextShow);
    }
  }, [markReadIfAtBottom]);

  useEffect(() => {
    const showSub = Keyboard.addListener(
      Platform.OS === "ios" ? "keyboardWillShow" : "keyboardDidShow",
      (event) => {
        if (Platform.OS === "android") {
          const nextHeight = Math.max(0, (event?.endCoordinates?.height ?? 0) - insets.bottom);
          setAndroidKeyboardHeight(nextHeight);
        }

        if (isNearBottomRef.current) {
          scheduleScroll(120);
        }
      }
    );

    const hideSub = Keyboard.addListener(
      Platform.OS === "ios" ? "keyboardWillHide" : "keyboardDidHide",
      () => {
        if (Platform.OS === "android") {
          setAndroidKeyboardHeight(0);
        }
        scheduleScroll(80);
      }
    );

    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, [insets.bottom, scheduleScroll]);

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
      const distanceFromBottom = contentSize.height - (contentOffset.y + layoutMeasurement.height);
      updateBottomState(distanceFromBottom);
    },
    [updateBottomState]
  );

  useEffect(() => {
    if (!conversationId) return;

    const unsubConversation = onSnapshot(
      doc(db, "conversations", conversationId),
      (snap) => {
        if (!snap.exists()) {
          setConversationData(null);
          return;
        }
        const nextConversation = { id: snap.id, ...(snap.data() as any) };

        setConversationData(nextConversation);

        if (user?.uid) {
          const members = Array.isArray(nextConversation.members) ? nextConversation.members : [];
          setDenied(!members.includes(user.uid));
        }
      },
      () => {
        setConversationData(null);
        setChatError("Cette conversation est introuvable ou inaccessible.");
      }
    );

    return () => unsubConversation();
  }, [conversationId, user?.uid]);

  useEffect(() => {
    if (!conversationId) return;

    setInitialLoading(true);
    setChatError(null);
    setDenied(false);
    setMessages([]);
    setOldestDoc(null);
    setHasMore(true);
    setShowScrollToBottom(false);
    setHasNewMessages(false);

    didInitialScrollRef.current = false;
    pendingInitialScrollRef.current = false;
    pendingAutoScrollRef.current = false;
    isNearBottomRef.current = true;
    lastMessageIdRef.current = null;
    lastReadMessageIdRef.current = null;
    newestMessageRef.current = null;
    oldestDocRef.current = null;
    loadedOlderRef.current = false;
    forceScrollAfterSendRef.current = false;
    scrollButtonVisibleRef.current = false;
    setAndroidKeyboardHeight(0);

    const qy = query(
      collection(db, "conversations", conversationId, "messages"),
      orderBy("createdAt", "desc"),
      limit(PAGE_SIZE)
    );

    const unsub = onSnapshot(
      qy,
      (snap) => {
        const docs = snap.docs;

        const items: Message[] = docs.map((d) => ({
          id: d.id,
          ...(d.data() as any),
        }));

        const latest = [...items].reverse();
        const newestMessage = latest.length ? latest[latest.length - 1] : null;
        const newestMessageId = newestMessage?.id ?? null;
        const previousLastMessageId = lastMessageIdRef.current;
        newestMessageRef.current = newestMessage;

        setMessages((prev) => {
          const prevMap = new Map(prev.map((m) => [m.id, m]));

          const latestStable = latest.map((m) => {
            const old = prevMap.get(m.id);

            if (
              old &&
              old.senderUid === m.senderUid &&
              old.text === m.text &&
              toMillis(old.createdAt) === toMillis(m.createdAt)
            ) {
              return old;
            }

            return m;
          });

          if (!prev.length) return latestStable;

          const latestIds = new Set(latestStable.map((m) => m.id));
          const olderOnly = prev.filter((m) => !latestIds.has(m.id));

          return [...olderOnly, ...latestStable];
        });

        if (!loadedOlderRef.current) {
          oldestDocRef.current = docs[docs.length - 1] || null;
          setOldestDoc(oldestDocRef.current);
          setHasMore(docs.length === PAGE_SIZE);
        }
        setInitialLoading(false);

        markReadIfAtBottom();

        if (!didInitialScrollRef.current) {
          didInitialScrollRef.current = true;
          lastMessageIdRef.current = newestMessageId;
          pendingInitialScrollRef.current = true;
          return;
        }

        const hasNewMessage =
          !!newestMessageId &&
          !!previousLastMessageId &&
          newestMessageId !== previousLastMessageId &&
          !loadingOlderRef.current;

        lastMessageIdRef.current = newestMessageId;

        if (hasNewMessage) {
          const isMyOwnNewMessage = newestMessage?.senderUid === user?.uid;

          if (isNearBottomRef.current || forceScrollAfterSendRef.current || isMyOwnNewMessage) {
            pendingAutoScrollRef.current = true;
          } else {
            setHasNewMessages(true);
          }
        }
      },
      (err) => {
        setInitialLoading(false);
        if (err?.code === "permission-denied") {
          setDenied(true);
        } else {
          setChatError(getChatErrorMessage(err, "Impossible de charger les messages."));
        }
      }
    );

    return () => unsub();
  }, [conversationId, markReadIfAtBottom, user?.uid]);

  useEffect(() => {
    if (!messages.length) return;

    if (pendingAutoScrollRef.current) {
      pendingAutoScrollRef.current = false;
      scrollToBottom(true);
      forceScrollAfterSendRef.current = false;
    }
  }, [messages.length, scrollToBottom]);

  const loadOlder = useCallback(async () => {
    const cursor = oldestDocRef.current || oldestDoc;
    if (!conversationId || denied || !hasMore || loadingOlderRef.current || !cursor) return;

    loadedOlderRef.current = true;
    loadingOlderRef.current = true;
    setLoadingOlder(true);

    try {
      const qy = query(
        collection(db, "conversations", conversationId, "messages"),
        orderBy("createdAt", "desc"),
        startAfter(cursor),
        limit(PAGE_SIZE)
      );

      const snap = await getDocs(qy);
      const docs = snap.docs;

      const items: Message[] = docs.map((d) => ({
        id: d.id,
        ...(d.data() as any),
      }));

      setMessages((prev) => {
        const older = [...items].reverse();
        const prevIds = new Set(prev.map((m) => m.id));
        const olderOnly = older.filter((m) => !prevIds.has(m.id));
        return [...olderOnly, ...prev];
      });

      oldestDocRef.current = docs[docs.length - 1] || cursor;
      setOldestDoc(oldestDocRef.current);
      setHasMore(docs.length === PAGE_SIZE);
      loadedOlderRef.current = docs.length > 0 || loadedOlderRef.current;
    } catch (error) {
      setChatError(getChatErrorMessage(error, "Impossible de charger les anciens messages."));
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  }, [conversationId, denied, hasMore, oldestDoc]);

  const onSendText = useCallback(
    async (value: string, messageId: string) => {
      if (!user?.uid || !conversationId) return false;

      if (denied) {
        Alert.alert("Accès refusé", "Tu n'as pas accès à cette conversation.");
        return false;
      }

      if (!isGroup) {
        Alert.alert("Archive de conversation", "Les anciens fils UID sont conservés en lecture seule. Ouvre un profil public pour démarrer une discussion avec une identité.");
        return false;
      }

      if (!canCurrentUserPost) {
        Alert.alert(
          "Messages bloqués",
          otherUserBlocked ? "Cette personne est bloquée." : "Seuls les admins de ce groupe peuvent envoyer des messages."
        );
        return false;
      }

      if (!value.trim()) return false;

      try {
        forceScrollAfterSendRef.current = true;
        await sendMessage(conversationId, user.uid, value, messageId);
        return true;
      } catch (e: any) {
        forceScrollAfterSendRef.current = false;

        if (e?.code === "permission-denied") {
          setDenied(true);
        Alert.alert("Accès refusé", "Tu n'as pas l'autorisation d'envoyer un message ici.");
        } else {
          const message = getChatErrorMessage(e, "Impossible d'envoyer le message.");
          setChatError(message);
          Alert.alert("Erreur", message);
        }
        return false;
      }
    },
    [user?.uid, conversationId, denied, isGroup, canCurrentUserPost, otherUserBlocked]
  );

  const visibleMessages = useMemo(() => {
    if (!isGroup && deletedAtMs > 0) {
      return messages.filter((message) => toMillis(message.createdAt) > deletedAtMs);
    }

    return messages;
  }, [deletedAtMs, isGroup, messages]);

  useEffect(() => {
    if (!visibleMessages.length) {
      spacerHeightRef.current = 0;
      setTopSpacerHeight(0);
    }
  }, [visibleMessages.length]);

  const messageRows = useMemo<MessageRow[]>(
    () =>
      visibleMessages.map((item, index) => {
        const previous = index > 0 ? visibleMessages[index - 1] : null;
        const next = index < visibleMessages.length - 1 ? visibleMessages[index + 1] : null;
        const mine = item.senderUid === user?.uid;
        const sameAsPrev = previous?.senderUid === item.senderUid;
        const sameAsNext = next?.senderUid === item.senderUid;
        const senderLabel = displaySenderLabel(userMap[item.senderUid]);

        return {
          item,
          mine,
          sameAsPrev,
          sameAsNext,
          showSender: isGroup && !mine && !sameAsPrev && !!senderLabel,
          senderLabel,
          showTime:
            !next ||
            next.senderUid !== item.senderUid ||
            Math.abs(toMillis(next.createdAt) - toMillis(item.createdAt)) > 5 * 60 * 1000,
          dateLabel:
            !previous || !isSameMessageDay(previous, item)
              ? formatDateSeparator(item.createdAt) || "Message en cours d'envoi"
              : "",
        };
      }),
    [isGroup, user?.uid, userMap, visibleMessages]
  );

  const renderItem = useCallback(
    ({ item }: { item: MessageRow }) => <MessageBubble row={item} />,
    []
  );

  const listHeader = useMemo(() => {
    const spacer = topSpacerHeight > 0 ? <View style={{ height: topSpacerHeight }} /> : null;

    if (!isGroup && deletedAtMs > 0) {
      return (
        <>
          {spacer}
          <View style={{ paddingBottom: 10 }} />
        </>
      );
    }

    return (
      <>
        {spacer}
        <View style={{ paddingBottom: 10 }}>
          {hasMore ? (
            <Pressable
              onPress={loadOlder}
              disabled={loadingOlder}
              style={{
                alignSelf: "center",
                paddingHorizontal: 14,
                paddingVertical: 10,
                borderRadius: 14,
                backgroundColor: surface.primary,
                borderWidth: 1,
                borderColor: BORDER,
                opacity: loadingOlder ? 0.7 : 1,
              }}
            >
              <Text style={{ color: ACCENT, fontWeight: "900" }}>
                {loadingOlder ? "Chargement…" : "Charger les anciens messages"}
              </Text>
            </Pressable>
          ) : messages.length > 0 ? (
            <Text style={{ textAlign: "center", color: uiText.muted }}>
              Plus de messages
            </Text>
          ) : null}
        </View>
      </>
    );
  }, [deletedAtMs, hasMore, isGroup, loadingOlder, loadOlder, messages.length, topSpacerHeight]);

  const photoUrl =
    typeof otherUser?.photoURL === "string" && otherUser.photoURL.trim().length > 0
      ? otherUser.photoURL.trim()
      : null;
  const photoSource = avatarSourceFor(
    photoUrl,
    otherUid || String(otherUser?.username || otherUser?.displayName || "message-peer")
  );

  const initial = isGroup
    ? (chatTitle?.trim()?.[0] || "G").toUpperCase()
    : typeof otherUser?.username === "string" && otherUser.username.trim().length > 0
    ? otherUser.username.trim()[0].toUpperCase()
    : typeof otherUser?.displayName === "string" && otherUser.displayName.trim().length > 0
    ? otherUser.displayName.trim()[0].toUpperCase()
    : "?";
  const otherUserIdentityLabel = getPrimaryIdentityBadge(otherUser)?.label;

  const handleConversationMenu = useCallback(() => {
    if (!user?.uid) return;

    if (isGroup) {
      Alert.alert("Options du groupe", "", [
        {
          text: "Voir les informations",
          onPress: () =>
            router.push({ pathname: "/(tabs)/messages/group/[id]", params: { id: conversationId } }),
        },
        { text: "Annuler", style: "cancel" },
      ]);
      return;
    }

    if (!otherUid) return;
    const isBlocked = blockedUsers.has(otherUid);
    Alert.alert("Options de la conversation", "", [
      {
        text: "Voir le profil",
        onPress: () => router.push({ pathname: "/profile/[id]", params: { id: otherUid } }),
      },
      {
        text: isBlocked ? "Débloquer" : "Bloquer",
        style: isBlocked ? "default" : "destructive",
        onPress: async () => {
          try {
            if (isBlocked) {
              await unblockUser({ uid: user.uid, blockedUid: otherUid });
            } else {
              await blockUser({ uid: user.uid, blockedUid: otherUid });
            }
          } catch (error) {
            Alert.alert("Erreur", getChatErrorMessage(error, "Impossible de modifier le blocage."));
          }
        },
      },
      {
        text: "Signaler",
        onPress: async () => {
          try {
            await reportUser({ reporterUid: user.uid, targetUid: otherUid, reason: "other" });
            Alert.alert("Signalement envoye", "Merci de nous aider a proteger la communaute.");
          } catch (error) {
            Alert.alert("Erreur", getChatErrorMessage(error, "Impossible d'envoyer le signalement."));
          }
        },
      },
      { text: "Annuler", style: "cancel" },
    ]);
  }, [blockedUsers, conversationId, isGroup, otherUid, user?.uid]);

  if (denied) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: BG }}>
        <View style={{ flex: 1, padding: 16, justifyContent: "center" }}>
          <Text style={{ color: uiText.primary, fontSize: 18, fontWeight: "900" }}>Accès refusé</Text>
          <Text style={{ color: uiText.muted, marginTop: 10, lineHeight: 20 }}>
            Tu n&apos;es pas membre de cette conversation ou tes permissions ont change.
          </Text>

          <Pressable
            onPress={() => router.replace("/(tabs)/messages")}
            style={{
              marginTop: 18,
              backgroundColor: ACCENT,
              paddingVertical: 12,
              borderRadius: 14,
              alignItems: "center",
            }}
          >
            <Text style={{ color: uiText.primary, fontWeight: "900" }}>Retour aux messages</Text>
          </Pressable>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: BG }}>
      {Platform.OS === "ios" ? (
        <KeyboardAvoidingView
          style={{ flex: 1 }}
          behavior="padding"
          keyboardVerticalOffset={insets.top}
        >
          <View style={{ flex: 1 }}>
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 12,
                paddingHorizontal: 16,
                paddingTop: 8,
                paddingBottom: 10,
              }}
            >
              <Pressable
                onPress={() => router.replace("/(tabs)/messages")}
                accessibilityRole="button"
                accessibilityLabel="Retour aux messages"
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  backgroundColor: CARD,
                  borderWidth: 1,
                  borderColor: BORDER,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Ionicons name="chevron-back" size={20} color={uiText.primary} />
              </Pressable>

              <Pressable
                onPress={() => {
                  if (isGroup) {
                    router.push({
                      pathname: "/(tabs)/messages/group/[id]",
                      params: { id: conversationId },
                    });
                  } else if (otherUid) {
                    router.push({ pathname: "/profile/[id]", params: { id: otherUid } });
                  }
                }}
                accessibilityLabel={isGroup ? "Ouvrir les informations du groupe" : "Ouvrir le profil"}
                accessibilityRole="button"
                style={{
                  flex: 1,
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                {!isGroup ? (
                  <Image
                    source={photoSource}
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 18,
                      backgroundColor: surface.primary,
                    }}
                    resizeMode="cover"
                  />
                ) : (
                  <View
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 18,
                      backgroundColor: surface.elevated,
                      borderWidth: 1,
                      borderColor: BORDER,
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Text style={{ color: uiText.primary, fontWeight: "900", fontSize: 14 }}>{initial}</Text>
                  </View>
                )}

                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text
                    style={{
                      color: uiText.primary,
                      fontSize: 18,
                      fontWeight: "900",
                    }}
                  >
                    {chatTitle}
                  </Text>

                  {isGroup ? (
                    <Text
                      numberOfLines={2}
                      style={{
                        color: uiText.muted,
                        marginTop: 2,
                        fontSize: 12,
                        fontWeight: "700",
                      }}
                    >
                      {groupMemberCount} membres
                    </Text>
                  ) : otherUserIdentityLabel ? (
                    <Text style={{ color: uiText.brand, marginTop: 2, fontSize: 11, fontWeight: "800" }}>
                      {otherUserIdentityLabel}
                    </Text>
                  ) : null}
                </View>

                {isGroup ? <Ionicons name="chevron-forward" size={18} color={uiText.muted} /> : null}
              </Pressable>
              <Pressable
                onPress={handleConversationMenu}
                accessibilityLabel="Ouvrir le menu de la conversation"
                accessibilityRole="button"
                hitSlop={8}
                style={{ width: 44, height: 44, alignItems: "center", justifyContent: "center" }}
              >
                <Ionicons name="ellipsis-horizontal" size={22} color={uiText.primary} />
              </Pressable>
            </View>

            <View style={{ flex: 1 }}>
              {chatError ? (
                <View
                  style={{
                    marginHorizontal: 16,
                    marginBottom: 6,
                    padding: 10,
                    borderRadius: 12,
                    backgroundColor: surface.danger,
                    borderWidth: 1,
                    borderColor: border.danger,
                  }}
                >
                  <Text style={{ color: uiText.danger, fontSize: 12 }}>{chatError}</Text>
                </View>
              ) : null}
              <FlatList
                ref={listRef}
                data={messageRows}
                keyExtractor={(row) => row.item.id}
                renderItem={renderItem}
                ListHeaderComponent={listHeader}
                keyboardShouldPersistTaps="handled"
                maintainVisibleContentPosition={{ minIndexForVisible: 0, autoscrollToTopThreshold: 12 }}
                onScroll={handleScroll}
                onLayout={(event) => {
                  const nextListHeight = Math.ceil(event.nativeEvent.layout.height);
                  if (nextListHeight > 0 && nextListHeight !== listHeight) {
                    setListHeight(nextListHeight);
                  }
                }}
                onContentSizeChange={(_, height) => {
                  const rawContentHeight = Math.max(0, height - spacerHeightRef.current);
                  const nextSpacer = visibleMessages.length
                    ? Math.max(0, listHeight - rawContentHeight - 6)
                    : 0;

                  if (nextSpacer !== spacerHeightRef.current) {
                    spacerHeightRef.current = nextSpacer;
                    setTopSpacerHeight(nextSpacer);
                  }

                  if (pendingInitialScrollRef.current) {
                    pendingInitialScrollRef.current = false;
                    scrollToBottom(false);
                  }
                }}
                scrollEventThrottle={16}
                initialNumToRender={12}
                maxToRenderPerBatch={12}
                windowSize={7}
                removeClippedSubviews={false}
                contentContainerStyle={{
                  paddingHorizontal: 16,
                  paddingTop: 6,
                  paddingBottom: composerHeight + 12,
                  flexGrow: 1,
                }}
                ListEmptyComponent={
                  initialLoading ? (
                    <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
                      <ActivityIndicator color={uiText.brand} />
                    </View>
                  ) : (
                    <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
                      <Text style={{ color: uiText.muted }}>Aucun message pour le moment.</Text>
                    </View>
                  )
                }
              />

              {showScrollToBottom ? (
                <Pressable
                  onPress={() => {
                    setHasNewMessages(false);
                    scrollToBottom(true);
                  }}
                  accessibilityRole="button"
                  accessibilityLabel="Revenir aux derniers messages"
                  style={{
                    position: "absolute",
                    right: 16,
                    bottom: composerHeight + 16,
                    width: 46,
                    height: 46,
                    borderRadius: 23,
                    backgroundColor: ACCENT,
                    borderWidth: 1,
                    borderColor: border.subtle,
                    alignItems: "center",
                    justifyContent: "center",
                    shadowColor: "#000",
                    shadowOpacity: 0.25,
                    shadowRadius: 10,
                    shadowOffset: { width: 0, height: 4 },
                    elevation: 8,
                  }}
                >
                  <Ionicons name="chevron-down" size={22} color={uiText.primary} />
                </Pressable>
              ) : null}
              {hasNewMessages ? (
                <Pressable
                  onPress={() => {
                    setHasNewMessages(false);
                    scrollToBottom(true);
                  }}
                  accessibilityRole="button"
                  accessibilityLabel="Voir les nouveaux messages"
                  style={{
                    position: "absolute",
                    alignSelf: "center",
                    bottom: composerHeight + 16,
                    paddingHorizontal: 14,
                    paddingVertical: 9,
                    borderRadius: 999,
                    backgroundColor: ACCENT,
                  }}
                >
                  <Text style={{ color: uiText.primary, fontWeight: "800", fontSize: 12 }}>Nouveaux messages</Text>
                </Pressable>
              ) : null}
            </View>

            {isGroup ? <ChatComposer
              onSendText={onSendText}
              scrollToBottom={scrollToBottom}
              disabled={!canCurrentUserPost}
              disabledLabel={otherUserBlocked ? "Cette personne est bloquée" : "Seuls les admins peuvent écrire ici"}
              bottomInset={composerBottomInset}
              onLayout={(event) => {
                const nextHeight = Math.ceil(event.nativeEvent.layout.height);
                if (nextHeight > 0 && nextHeight !== composerHeight) {
                  setComposerHeight(nextHeight);
                }
              }}
            /> : conversationData ? <LegacyDirectArchiveNotice /> : null}
          </View>
        </KeyboardAvoidingView>
      ) : (
        <View style={{ flex: 1, paddingBottom: androidKeyboardHeight + (androidKeyboardHeight > 0 ? insets.bottom : 0) }}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 12,
              paddingHorizontal: 16,
              paddingTop: 8,
              paddingBottom: 10,
            }}
          >
            <Pressable
              onPress={() => router.replace("/(tabs)/messages")}
              accessibilityRole="button"
              accessibilityLabel="Retour aux messages"
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                backgroundColor: CARD,
                borderWidth: 1,
                borderColor: BORDER,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Ionicons name="chevron-back" size={20} color={uiText.primary} />
            </Pressable>

            <Pressable
              onPress={() => {
                if (isGroup) {
                  router.push({
                    pathname: "/(tabs)/messages/group/[id]",
                    params: { id: conversationId },
                  });
                } else if (otherUid) {
                  router.push({ pathname: "/profile/[id]", params: { id: otherUid } });
                }
              }}
              accessibilityLabel={isGroup ? "Ouvrir les informations du groupe" : "Ouvrir le profil"}
              accessibilityRole="button"
              style={{
                flex: 1,
                flexDirection: "row",
                alignItems: "center",
                gap: 12,
              }}
            >
              {!isGroup ? (
                <Image
                  source={photoSource}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 18,
                    backgroundColor: surface.primary,
                  }}
                  resizeMode="cover"
                />
              ) : (
                <View
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 18,
                    backgroundColor: surface.elevated,
                    borderWidth: 1,
                    borderColor: BORDER,
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Text style={{ color: uiText.primary, fontWeight: "900", fontSize: 14 }}>{initial}</Text>
                </View>
              )}

              <View style={{ flex: 1, minWidth: 0 }}>
                <Text
                  style={{
                    color: uiText.primary,
                    fontSize: 18,
                    fontWeight: "900",
                  }}
                >
                  {chatTitle}
                </Text>

                {isGroup ? (
                  <Text
                    numberOfLines={2}
                    style={{
                      color: uiText.muted,
                      marginTop: 2,
                      fontSize: 12,
                      fontWeight: "700",
                    }}
                  >
                    {groupMemberCount} membres
                  </Text>
                ) : otherUserIdentityLabel ? (
                  <Text style={{ color: uiText.brand, marginTop: 2, fontSize: 11, fontWeight: "800" }}>
                    {otherUserIdentityLabel}
                  </Text>
                ) : null}
              </View>

              {isGroup ? <Ionicons name="chevron-forward" size={18} color={uiText.muted} /> : null}
            </Pressable>
            <Pressable
              onPress={handleConversationMenu}
              accessibilityLabel="Ouvrir le menu de la conversation"
              accessibilityRole="button"
              hitSlop={8}
              style={{ width: 44, height: 44, alignItems: "center", justifyContent: "center" }}
            >
              <Ionicons name="ellipsis-horizontal" size={22} color={uiText.primary} />
            </Pressable>
          </View>

          <View style={{ flex: 1 }}>
            {chatError ? (
              <View
                style={{
                  marginHorizontal: 16,
                  marginBottom: 6,
                  padding: 10,
                  borderRadius: 12,
                  backgroundColor: surface.danger,
                  borderWidth: 1,
                  borderColor: border.danger,
                }}
              >
                <Text style={{ color: uiText.danger, fontSize: 12 }}>{chatError}</Text>
              </View>
            ) : null}
            <FlatList
              ref={listRef}
              data={messageRows}
              keyExtractor={(row) => row.item.id}
              renderItem={renderItem}
              ListHeaderComponent={listHeader}
              keyboardShouldPersistTaps="handled"
              maintainVisibleContentPosition={{ minIndexForVisible: 0, autoscrollToTopThreshold: 12 }}
              onScroll={handleScroll}
              onLayout={(event) => {
                const nextListHeight = Math.ceil(event.nativeEvent.layout.height);
                if (nextListHeight > 0 && nextListHeight !== listHeight) {
                  setListHeight(nextListHeight);
                }
              }}
              onContentSizeChange={(_, height) => {
                const rawContentHeight = Math.max(0, height - spacerHeightRef.current);
                const nextSpacer = visibleMessages.length
                  ? Math.max(0, listHeight - rawContentHeight - 6)
                  : 0;

                if (nextSpacer !== spacerHeightRef.current) {
                  spacerHeightRef.current = nextSpacer;
                  setTopSpacerHeight(nextSpacer);
                }

                if (pendingInitialScrollRef.current) {
                  pendingInitialScrollRef.current = false;
                  scrollToBottom(false);
                }
              }}
              scrollEventThrottle={16}
              initialNumToRender={12}
              maxToRenderPerBatch={12}
              windowSize={7}
              removeClippedSubviews={Platform.OS === "android"}
              contentContainerStyle={{
                paddingHorizontal: 16,
                paddingTop: 6,
                paddingBottom: composerHeight + 12,
                flexGrow: 1,
              }}
              ListEmptyComponent={
                initialLoading ? (
                  <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
                    <ActivityIndicator color={uiText.brand} />
                  </View>
                ) : (
                  <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
                    <Text style={{ color: uiText.muted }}>Aucun message pour le moment.</Text>
                  </View>
                )
              }
            />

            {showScrollToBottom ? (
              <Pressable
                onPress={() => {
                  setHasNewMessages(false);
                  scrollToBottom(true);
                }}
                accessibilityRole="button"
                accessibilityLabel="Revenir aux derniers messages"
                style={{
                  position: "absolute",
                  right: 16,
                  bottom: composerHeight + 16,
                  width: 46,
                  height: 46,
                  borderRadius: 23,
                  backgroundColor: ACCENT,
                  borderWidth: 1,
                  borderColor: border.subtle,
                  alignItems: "center",
                  justifyContent: "center",
                  shadowColor: "#000",
                  shadowOpacity: 0.25,
                  shadowRadius: 10,
                  shadowOffset: { width: 0, height: 4 },
                  elevation: 8,
                }}
              >
                <Ionicons name="chevron-down" size={22} color={uiText.primary} />
              </Pressable>
            ) : null}
            {hasNewMessages ? (
              <Pressable
                onPress={() => {
                  setHasNewMessages(false);
                  scrollToBottom(true);
                }}
                accessibilityRole="button"
                accessibilityLabel="Voir les nouveaux messages"
                style={{
                  position: "absolute",
                  alignSelf: "center",
                  bottom: composerHeight + 16,
                  paddingHorizontal: 14,
                  paddingVertical: 9,
                  borderRadius: 999,
                  backgroundColor: ACCENT,
                }}
              >
                <Text style={{ color: uiText.primary, fontWeight: "800", fontSize: 12 }}>Nouveaux messages</Text>
              </Pressable>
            ) : null}
          </View>

          {isGroup ? <ChatComposer
            onSendText={onSendText}
            scrollToBottom={scrollToBottom}
            disabled={!canCurrentUserPost}
              disabledLabel={otherUserBlocked ? "Cette personne est bloquée" : "Seuls les admins peuvent écrire ici"}
            bottomInset={composerBottomInset}
            onLayout={(event) => {
              const nextHeight = Math.ceil(event.nativeEvent.layout.height);
              if (nextHeight > 0 && nextHeight !== composerHeight) {
                setComposerHeight(nextHeight);
              }
            }}
          /> : conversationData ? <LegacyDirectArchiveNotice /> : null}
        </View>
      )}
    </SafeAreaView>
  );
}
